import { fetchDurableJob, fetchDurableJobs, fetchDurableJobsList, fetchUnseenDurableJobs, fetchUserConfirmations } from '@/api/durable-jobs.api';
import {
    type DurableJobExpandOpts,
    DurableJobStatus,
    type IDurableJob,
    type IFetchDurableJobsParams,
    type IUserConfirmation,
    UserConfirmationStatus,
} from '@condo/domain';
import { type Row, Shape, ShapeStream } from '@electric-sql/client';
import { useQuery } from '@tanstack/vue-query';
import { camelCase, isNil, mapKeys, orderBy } from 'lodash-es';
import { type MaybeRef, type Ref, computed, onBeforeUnmount, ref, unref, watch } from 'vue';
import { useRootAppConfig } from './config.hooks';
import { useSelfUser } from './user.hooks';

export const useDurableJobs = (paramsOrRef?: MaybeRef<IFetchDurableJobsParams>) => {
    const params = computed(() => unref(paramsOrRef));
    return useQuery(['durable-jobs', params], async () => fetchDurableJobs(params.value), {
        initialData: {
            durableJobs: [],
            total: 0,
        },
        refetchInterval: 5_000,
    });
};

const PENDING_STATUSES = [DurableJobStatus.pending, DurableJobStatus.scheduled];

export const useDurableJobsWithRefetchOnSuccess = (paramsOrRef: MaybeRef<IFetchDurableJobsParams>, onJobSuccess: () => void) => {
    const result = useDurableJobs(paramsOrRef);
    const previousStatuses = ref<Map<string, DurableJobStatus>>(new Map());
    watch(
        () => result.data.value?.durableJobs,
        jobs => {
            if (!jobs) return;
            const currentStatuses = new Map<string, DurableJobStatus>();
            for (const job of jobs) {
                currentStatuses.set(job.id!, job.status!);
                const previousStatus = previousStatuses.value.get(job.id!);
                if (previousStatus && PENDING_STATUSES.includes(previousStatus) && job.status === DurableJobStatus.success) {
                    onJobSuccess();
                }
            }
            previousStatuses.value = currentStatuses;
        },
        { deep: true },
    );
    return result;
};

export const useDurableJob = (idOrRef: MaybeRef<string | null>, opts?: { refetchInterval?: number }) => {
    const enabled = computed(() => !!unref(idOrRef));
    return useQuery(['durable-job-by-id', idOrRef], async () => fetchDurableJob(unref(idOrRef) ?? ''), {
        initialData: null,
        enabled,
        refetchInterval: opts?.refetchInterval,
    });
};

export const useUnseenDurableJobs = (expand?: DurableJobExpandOpts): [{ tasks: Ref<IUserConfirmation[]>; jobs: Ref<IDurableJob[]> }, () => Promise<any>] => {
    const config = useRootAppConfig();

    if (config.value?.JOBS_PROVIDER === 'electric') {
        console.log('[Durable Jobs] Using electricsql');
        const jobsResult = useElectricJobs();
        const tasksResult = useElectricUserConfirmationTasks();

        onBeforeUnmount(() => {
            jobsResult.subscription.unsubscribe();
            tasksResult.subscription.unsubscribe();
        });

        return [{ tasks: tasksResult.tasks, jobs: jobsResult.jobs }, () => Promise.resolve()];
    }

    console.log('[Durable Jobs] Using polling');

    const refetchJitterMinMS = 500;
    const refetchJitterMaxMS = 1500;

    const jobsQuery = useQuery(['durable-jobs', 'unseen'], async () => fetchUnseenDurableJobs(expand), {
        initialData: {
            durableJobs: [],
            total: 0,
        },
        refetchInterval: () => {
            const jitter = Math.floor(Math.random() * (refetchJitterMaxMS - refetchJitterMinMS + 1) + refetchJitterMinMS);
            return config.value!.JOBS_POLLING_INTERVAL_MS + jitter;
        },
    });
    const userConfirmationsQuery = useQuery(['user-confirmations'], async () => fetchUserConfirmations(), {
        initialData: [],
        refetchInterval: () => {
            const jitter = Math.floor(Math.random() * (refetchJitterMaxMS - refetchJitterMinMS + 1) + refetchJitterMinMS);
            return config.value!.JOBS_POLLING_INTERVAL_MS + jitter;
        },
    });

    const durableJobs = computed(() => jobsQuery.data.value.durableJobs.filter(j => isNil(j.silent) || !j.silent));
    const tasks = computed(() => userConfirmationsQuery.data.value);
    return [
        { jobs: durableJobs, tasks },
        () => {
            return Promise.all([jobsQuery.refetch(), userConfirmationsQuery.refetch()]);
        },
    ];
};

export const useDurableJobsList = () => {
    return useQuery(['durable-jobs', 'list'], async () => fetchDurableJobsList(), {
        initialData: [],
        refetchInterval: 5_000,
    });
};

const notificationStatuses = [DurableJobStatus.pending, DurableJobStatus.fail, DurableJobStatus.success];

export const useElectricJobs = () => {
    const user = useSelfUser();
    const config = useRootAppConfig();
    const jobs = ref<IDurableJob[]>([]);

    if (!user.value?.userId) {
        throw new Error(`Must have userId attached to self-user to watch jobs`);
    }
    if (!config.value?.S_ELECTRIC_URL) {
        throw new Error(`Must have S_ELECTRIC_URL defined in config when using electric-sql`);
    }

    // @todo it somehow doesnt consistently work w/ --> AND "status" IN (${notificationStatuses.map(s => `'${s}'`).join(',')})
    const stream = new ShapeStream<Row<IDurableJob>>({
        url: `${config.value.S_ELECTRIC_URL}/v1/shape`,
        headers:
            config.value.ELECTRIC_USERNAME && config.value.ELECTRIC_PASSWORD
                ? {
                      Authorization: `Basic ${btoa(`${config.value.ELECTRIC_USERNAME}:${config.value.ELECTRIC_PASSWORD}`)}`,
                  }
                : undefined,
        params: {
            table: `__durable_jobs`,
            where: `created_by='${user.value.userId}' AND seen_at IS NULL`,
            columns: [
                'id',
                'name',
                'status',
                'step',
                'total_steps',
                'step_name',
                'created_by',
                'started_at',
                'scheduled_at',
                'finished_at',
                'seen_at',
                'silent',
                'updated_at',
                'created_at',
                'meta',
            ],
        },
    });
    const shape = new Shape(stream);
    const unsubscribe = shape.subscribe(payload => {
        if (!payload.rows.length) {
            jobs.value = [];
            return;
        }
        const newJobs = payload.rows.map(m => mapKeys(m as Record<string, any>, (_k, v) => camelCase(v)) as IDurableJob);
        jobs.value = orderBy(
            newJobs.filter(j => notificationStatuses.includes(j.status) && !j.seenAt && (isNil(j.silent) || !j.silent)),
            'createdAt',
            'desc',
        );
    });

    return { subscription: { unsubscribe }, jobs };
};
export const useElectricUserConfirmationTasks = () => {
    const user = useSelfUser();
    const config = useRootAppConfig();
    const tasks = ref<IUserConfirmation[]>([]);

    if (!user.value?.userId) {
        throw new Error(`Must have userId attached to self-user to watch user-confirmation-tasks`);
    }
    if (!config.value?.S_ELECTRIC_URL) {
        throw new Error(`Must have S_ELECTRIC_URL defined in config when using electric-sql`);
    }

    const stream = new ShapeStream<Row<IUserConfirmation>>({
        url: `${config.value.S_ELECTRIC_URL}/v1/shape`,
        headers:
            config.value.ELECTRIC_USERNAME && config.value.ELECTRIC_PASSWORD
                ? {
                      Authorization: `Basic ${btoa(`${config.value.ELECTRIC_USERNAME}:${config.value.ELECTRIC_PASSWORD}`)}`,
                  }
                : undefined,
        params: {
            table: `__user_confirmations`,
            where: `user_id='${user.value.userId}' AND status = '${UserConfirmationStatus.CREATED}'`,
            columns: ['user_confirmation_id', 'task', 'durable_job_id', 'status', 'params', 'origin'],
        },
    });
    const shape = new Shape(stream);

    const unsubscribe = shape.subscribe(payload => {
        if (!payload.rows.length) {
            tasks.value = [];
            return;
        }
        tasks.value = payload.rows.map(m => mapKeys(m as Record<string, any>, (_k, v) => camelCase(v)) as IUserConfirmation);
    });

    return { subscription: { unsubscribe }, tasks };
};
